import { ApolloError, useMutation } from "@apollo/client";
import { Alert, Box, Button, Stack, useTheme } from "@mui/material";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { StateFieldsForm } from "components/Forms/StateFieldsForm";
import { CatalogLink } from "components/ModelLinks/CatalogLink";
import { FacilityChemicalLink } from "components/ModelLinks/FacilityChemicalLink";
import { ProductInventoryLink } from "components/ModelLinks/ProductInventoryLink";
import { SkeletonFormGroup } from "components/Skeleton";
import { ThresholdChip } from "components/ThresholdChip";
import { EXEMPT_REASON_LABELS, EXEMPT_REASON_ORDER } from "encamp-shared/src/tierii/exemptReason";
import { gql } from "generated-graphql";
import {
  IssueLevel,
  ProgramArea,
  ReportingFacilityChemicalQuery,
  ExemptReason,
} from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformStateField";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useMeasurements } from "hooks/useMeasurements";
import { useValidatingForm } from "hooks/useValidatingForm";
import { omit } from "lodash-es";
import { Fragment, useCallback, useMemo, useState } from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { anySegment } from "util/issues";

import { GET_REPORT } from "../Report/useReport";
import { useReportIsVerified } from "../Report/useReportIsVerified";
import { getExemptValidationIssues } from "../Report/validationHooks/exemptValidationIssues";
import { getReportingUnitOverrideValidationIssues } from "../Report/validationHooks/reportingUnitOverrideValidationIssues";
import { useReportingFacilityChemicalInputValidation } from "../Report/validationHooks/useReportingFacilityChemicalInputValidation";
import { resolveOverrideWrite } from "./buildOverrideFromForm";
import { DisclosurePanel } from "./DisclosurePanel";
import { MeasurementsTable } from "./MeasurementsTable";
import { ProductsTable } from "./ProductsTable";
import { REPORTING_FACILITY_CHEMICAL_QUERY } from "./ReportingFacilityChemicalEditor";
import { FormValues } from "./reportingFacilityChemicalFormValues";
import { REPORTING_FACILITY_CHEMICALS_QUERY } from "./ReportingFacilityChemicalTable";
import { shouldOfferReportingUnitOverride } from "./reportingUnitOverrideEligibility";
import { StaffReportingUnitOverride } from "./StaffReportingUnitOverride";
import { StorageLocationsTable } from "./StorageLocationsTable";
import { SummaryCard } from "./SummaryCard";

type AggregationError = {
  problematicChemicals?: { id: string; name: string }[];
};

const UPSERT_FACILITY_CHEMICAL_STATE_FIELDS = gql(`
  mutation UpsertFacilityChemicalStateFields($input: [FacilityChemicalStateFieldInput!]!, $facilityId: ID!, $chemicalId: ID!, $reportingYear: Int!) {
    upsertFacilityChemicalStateFields(input: $input, facilityId: $facilityId, chemicalId: $chemicalId, reportingYear: $reportingYear, type: EPCRA) {
      id
    }
  }
`);

const SET_IS_ALWAYS_REPORTED = gql(`
  mutation SetIsAlwaysReportedOnRfc($id: ID!, $isAlwaysReported: Boolean) {
    setIsAlwaysReportedOnReportingFacilityChemical(id: $id, isAlwaysReported: $isAlwaysReported) {
      id
      isAlwaysReported
    }
  }
`);

const SET_IS_EXEMPT = gql(`
  mutation SetIsExemptOnRfc($id: ID!, $isExempt: Boolean, $isExemptReason: ExemptReason, $isExemptNote: String) {
    setIsExemptOnReportingFacilityChemical(id: $id, isExempt: $isExempt, isExemptReason: $isExemptReason, isExemptNote: $isExemptNote) {
      id
      isExempt
      isExemptReason
      isExemptNote
    }
  }
`);

const SET_REPORTING_UNIT_OVERRIDE = gql(`
  mutation SetReportingUnitOverride($facilityId: ID!, $chemicalId: ID!, $unit: UnitType!, $reason: String!, $reportingYear: Int!) {
    setReportingUnitOverride(facilityId: $facilityId, chemicalId: $chemicalId, unit: $unit, reason: $reason, reportingYear: $reportingYear) {
      id
      unit
      reason
    }
  }
`);

const CLEAR_REPORTING_UNIT_OVERRIDE = gql(`
  mutation ClearReportingUnitOverride($facilityId: ID!, $chemicalId: ID!, $reportingYear: Int!) {
    clearReportingUnitOverride(facilityId: $facilityId, chemicalId: $chemicalId, reportingYear: $reportingYear)
  }
`);

export const ReportingFacilityChemicalDialog = ({
  rfc,
  loading: loadingRfc,
  onClose,
}: {
  rfc: ReportingFacilityChemicalQuery["reportingFacilityChemical"] | undefined;
  loading: boolean;
  onClose: () => void;
}) => {
  const isVerified = useReportIsVerified();
  const theme = useTheme();
  const [upsertStateFields, { loading: savingStateFields }] = useMutation(
    UPSERT_FACILITY_CHEMICAL_STATE_FIELDS
  );
  const [setIsAlwaysReported, { loading: savingIsAlwaysReported }] =
    useMutation(SET_IS_ALWAYS_REPORTED);
  const [setIsExempt, { loading: savingIsExempt }] = useMutation(SET_IS_EXEMPT);
  const [setReportingUnitOverride, { loading: savingOverride }] = useMutation(
    SET_REPORTING_UNIT_OVERRIDE
  );
  const [clearReportingUnitOverride, { loading: clearingOverride }] = useMutation(
    CLEAR_REPORTING_UNIT_OVERRIDE
  );

  const { tenantId, reportId } = useParams();
  const { isStaff } = useCurrentUser();

  // The resolver refuses an override it cannot honour with a UserFacingError.
  // Held here so the dialog can stay open and show it rather than closing on a
  // save that did not fully happen.
  const [overrideError, setOverrideError] = useState<string | null>(null);

  // Matches what StaffReportingUnitOverride renders: staff only, and read-only
  // once the report is verified. Anyone else's submit must leave the override
  // exactly as they found it.
  const canEditOverride = isStaff && !isVerified;

  // A pounds-only jurisdiction leaves nothing to override into, so the
  // checkbox is withheld outright rather than leading staff into a select
  // whose only enabled option is the default. An existing override always
  // stays visible so it can be cleared.
  const offerOverride = shouldOfferReportingUnitOverride(rfc);

  const saving =
    savingStateFields ||
    savingIsAlwaysReported ||
    savingIsExempt ||
    savingOverride ||
    clearingOverride;

  // The refetch queries an override write should trigger once it completes,
  // shared by the set and clear paths below, matching how upsertStateFields
  // refetches after its own write.
  //
  // Unlike the other mutations on this dialog, setReportingUnitOverride and
  // clearReportingUnitOverride don't touch the RFC row directly — they write
  // the override table, then aggregation reads it back to recompute the RFC.
  // Both mutations now aggregate the reporting year passed in `reportingYear`
  // (this dialog always passes `rfc.reportingYear`, the year in view) inline
  // before returning, so a refetch right after either resolves does reflect
  // the override for that year. Every other reporting year the override could
  // affect is still only queued as a CHEMICAL_AGGREGATION job and applies
  // asynchronously — a refetch cannot observe those until that job runs.
  const overrideRefetchQueries = useMemo(
    () => [REPORTING_FACILITY_CHEMICAL_QUERY, REPORTING_FACILITY_CHEMICALS_QUERY, GET_REPORT],
    []
  );

  const defaultValues = useMemo<FormValues>(
    () => ({
      isAlwaysReported: rfc?.isAlwaysReported ?? false,
      isExempt: rfc?.isExempt ?? false,
      isExemptReason: rfc?.isExemptReason ?? null,
      isExemptNote: rfc?.isExemptNote ?? null,
      stateFields:
        rfc?.stateFields.map((sf) => ({
          ...omit(sf, "selectOptions", "tooltip"),
          value: sf.value ?? null,
          type: ProgramArea.Epcra,
        })) ?? [],
      // A chemical that already carries an override opens with the panel
      // expanded, so staff land on what's already been set rather than a
      // collapsed checkbox hiding an active override.
      reportingUnitOverrideEnabled: rfc?.reportingUnitOverride != null,
      reportingUnitOverride: rfc?.reportingUnitOverride?.unit ?? null,
      reportingUnitOverrideReason: rfc?.reportingUnitOverride?.reason ?? null,
    }),
    [rfc]
  );

  const { hasMeasurements } = useMeasurements(rfc?.facilityId ?? "");

  const uniqueProducts = useMemo(() => {
    if (!rfc) return [];

    if (hasMeasurements) {
      // Measurement mode: use catalog product IDs from measurements
      const products = (rfc.origin?.facilityProductMeasurements ?? [])
        .filter(
          (fpm): fpm is typeof fpm & { product: { id: string; name: string } } =>
            !!fpm?.product?.id && !!fpm?.product?.name
        )
        .map((fpm) => ({
          catalogProductId: fpm.product.id,
          inventoryLinkId: fpm.product.id, // Same as catalog in measurement mode
          name: fpm.product.name,
        }));

      const productsMap = new Map(products.map((p) => [p.catalogProductId, p]));
      return Array.from(productsMap.values());
    } else {
      // Non-measurement mode: use facility product IDs for inventory links
      const products = (rfc.origin?.facilityProducts ?? [])
        .filter(
          (fp): fp is typeof fp & { product: { id: string; name: string } } =>
            !!fp?.id && !!fp?.product?.id && !!fp?.product?.name
        )
        .map((fp) => ({
          catalogProductId: fp.product.id,
          inventoryLinkId: fp.id, // Facility product ID for inventory link
          name: fp.product.name,
        }));

      const productsMap = new Map(products.map((p) => [p.catalogProductId, p]));
      return Array.from(productsMap.values());
    }
  }, [rfc, hasMeasurements]);

  const hasChemicalOrigin = useMemo(
    () =>
      !!rfc?.origin?.facilityChemicalId ||
      (rfc?.origin?.facilityChemicalMeasurements?.length ?? 0) > 0,
    [rfc?.origin?.facilityChemicalId, rfc?.origin?.facilityChemicalMeasurements?.length]
  );

  const problematicChemicals = useMemo(
    () => (rfc?.aggregationError as AggregationError)?.problematicChemicals,
    [rfc?.aggregationError]
  );

  const form = useValidatingForm<FormValues>(
    defaultValues,
    rfc?.issues.filter(
      (issue) => !anySegment(issue, (s) => s.model === "ReportingFacilityChemicalOrigin")
    ),
    useReportingFacilityChemicalInputValidation(rfc),
    undefined,
    {
      isDisplayOnly: isVerified,
    }
  );

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async (data) => {
      if (!rfc) return;

      // The exempt reason/note and override reason errors are shown live via the
      // validator (getExemptValidationIssues,
      // getReportingUnitOverrideValidationIssues); block the save here too since
      // the validator resolver is debounced and may not have run before a fast
      // submit.
      if (
        getExemptValidationIssues(data, "ReportingFacilityChemical").length > 0 ||
        (canEditOverride &&
          getReportingUnitOverrideValidationIssues(data, "ReportingFacilityChemical", rfc, isStaff)
            .length > 0)
      ) {
        return;
      }

      // Set by either override mutation's `onError` below. A plain local, not
      // state: it has to be readable synchronously right after the await to
      // decide whether to close, and a setState would not have landed yet.
      let overrideFailed = false;
      const onOverrideError = (error: ApolloError) => {
        overrideFailed = true;
        setOverrideError(error.message);
      };
      setOverrideError(null);

      const overrideWrite = resolveOverrideWrite(data, {
        canEdit: canEditOverride,
        // Without this, every staff save of this dialog asked the server to
        // clear an override that was never set — an awaited inline aggregation
        // plus five queued jobs per save. Deliberately the override row rather
        // than `preOverrideUnit`, so a refused override stays clearable.
        hasExistingOverride: rfc.reportingUnitOverride != null,
      });
      // A unit was selected but the reason was missing (or whitespace-only).
      // Defence in depth behind the validator guard above: the reason is
      // required, and a save that silently dropped it would also drop every
      // other edit on this dialog.
      if (overrideWrite.kind === "block") {
        return;
      }

      const isExempt = data.isExempt ?? false;

      await setIsAlwaysReported({
        variables: { id: rfc.id, isAlwaysReported: data.isAlwaysReported },
      });

      // Clear the reason/note when not exempt — the conditional inputs keep
      // their last values in form state after being hidden.
      await setIsExempt({
        variables: {
          id: rfc.id,
          isExempt,
          isExemptReason: isExempt ? data.isExemptReason : null,
          isExemptNote: isExempt ? data.isExemptNote : null,
        },
      });

      await upsertStateFields({
        variables: {
          input: data.stateFields.map((stateField) => {
            return {
              ...transform(stateField),
              facilityId: rfc.facilityId,
              chemicalId: rfc.chemicalId,
              reportingYear: rfc.reportingYear,
            };
          }),
          facilityId: rfc.facilityId,
          chemicalId: rfc.chemicalId,
          reportingYear: rfc.reportingYear,
        },
        refetchQueries: overrideRefetchQueries,
      });

      // Last, so a failed mutation above leaves the override as it was rather
      // than applying an override for a save that did not happen.
      // `reportingYear` tells the resolver which year to aggregate inline
      // before returning — see the note on `overrideRefetchQueries` above.
      //
      // The resolver refuses an override it cannot honour with a
      // UserFacingError, and that has to reach the staff member who asked for
      // it. Both calls therefore take an `onError` — without one Apollo
      // rethrows, which would reject this handler as an unhandled rejection —
      // and the dialog stays open with the message so the save can be
      // corrected. Closing is deferred to the end for the same reason: a
      // dialog that has already closed can neither show the error nor reflect
      // that the override was never written.
      if (overrideWrite.kind === "set") {
        await setReportingUnitOverride({
          variables: {
            facilityId: rfc.facilityId,
            chemicalId: rfc.chemicalId,
            unit: overrideWrite.unit,
            reason: overrideWrite.reason,
            reportingYear: rfc.reportingYear,
          },
          refetchQueries: overrideRefetchQueries,
          onError: onOverrideError,
        });
      } else if (overrideWrite.kind === "clear") {
        await clearReportingUnitOverride({
          variables: {
            facilityId: rfc.facilityId,
            chemicalId: rfc.chemicalId,
            reportingYear: rfc.reportingYear,
          },
          refetchQueries: overrideRefetchQueries,
          onError: onOverrideError,
        });
      }

      if (overrideFailed) {
        return;
      }
      onClose();
    },
    [
      canEditOverride,
      clearReportingUnitOverride,
      isStaff,
      onClose,
      overrideRefetchQueries,
      rfc,
      setIsAlwaysReported,
      setIsExempt,
      setReportingUnitOverride,
      upsertStateFields,
    ]
  );

  const renderIssueListButton = () => {
    if (rfc?.aggregationErrorMessage) {
      return (
        <IssueListButton
          issues={[
            {
              message: rfc.aggregationErrorMessage,
              level: IssueLevel.Critical,
              modelName: "ReportingFacilityChemical",
            },
          ]}
        />
      );
    }

    if (form.issues) {
      return (
        <IssueListButton
          issues={form.issues.filter(
            (issue) => !anySegment(issue, (s) => s.model === "ReportingFacilityChemicalOrigin")
          )}
        />
      );
    }

    return null;
  };

  // Watched once here so both checkboxes' mutual-exclusion logic (each
  // disables the other) and the conditional panels below can share the same
  // values without re-deriving them per-JSX-branch.
  const isExempt = form.watch("isExempt") ?? false;
  const reportingUnitOverrideEnabled = form.watch("reportingUnitOverrideEnabled");

  // The exempt/override mutual exclusion is a staff-facing affordance and must
  // stay one. `reportingUnitOverrideEnabled` defaults from
  // `rfc.reportingUnitOverride != null` for *every* user, so without the
  // isStaff gate a staff-set override locked a customer out of marking their own
  // chemical exempt — with a tooltip citing an override whose panel is hidden
  // from them and which only staff can clear. Nothing about the exclusion is
  // enforced server-side by either mutation anyway, so this is purely about not
  // presenting a customer with a dead end they cannot resolve.
  const overrideBlocksExempt = isStaff && reportingUnitOverrideEnabled && !isExempt;

  const reportingUnitOverride = form.watch("reportingUnitOverride");
  const reportingUnitOverrideReason = form.watch("reportingUnitOverrideReason");

  // Whether submitting right now would be a no-op, so the Save button can be
  // disabled instead of accepting a click that silently returns. `onSubmit`
  // keeps its own guard: the validator resolver is debounced and a fast
  // submit can beat it, so this is the affordance and that is the enforcement.
  //
  // Scoped to the override deliberately. The exempt-reason validation has the
  // same dead-click today and is not addressed here — changing it would put an
  // unrelated regression risk into this change.
  const overrideBlocksSave = useMemo(() => {
    if (!canEditOverride || !reportingUnitOverrideEnabled) {
      return false;
    }
    // No unit selected is a valid, saveable state, not a blocked one:
    // resolveOverrideWrite reads it as "clear any existing override", which
    // makes picking "Use default" the natural way to remove one. Blocking it
    // would kill that path and leave Save dead with nothing to fix.
    return (
      getReportingUnitOverrideValidationIssues(
        {
          reportingUnitOverrideEnabled,
          reportingUnitOverride,
          reportingUnitOverrideReason,
        },
        "ReportingFacilityChemical",
        rfc,
        isStaff
      ).length > 0
    );
  }, [
    canEditOverride,
    isStaff,
    reportingUnitOverride,
    reportingUnitOverrideEnabled,
    reportingUnitOverrideReason,
    rfc,
  ]);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      title={
        rfc ? (
          <div>
            {`${rfc.reportingYear} Reporting Summary for ${rfc.chemical?.name}`}
            <ThresholdChip
              isOverThreshold={rfc.isOverThreshold}
              errorMessage={rfc.aggregationErrorMessage}
              loading={loadingRfc}
              sx={{ ml: 1 }}
            />
          </div>
        ) : (
          "Loading..."
        )
      }
      noPrimaryAction={isVerified ?? false}
      additionalPrimaryActionElements={
        isVerified
          ? [
              <Button key="done-button" onClick={onClose}>
                Done
              </Button>,
            ]
          : [renderIssueListButton()]
      }
      primaryActionButtonProps={{
        loading: saving,
        disabled: loadingRfc || overrideBlocksSave,
        onClick: form.handleSubmit(onSubmit),
      }}
    >
      {!rfc && <SkeletonFormGroup count={8} />}
      {rfc && (
        <Stack spacing={4}>
          <Stack spacing={1.5}>
            {!rfc.isOverThreshold && (
              <CheckboxField
                label="Voluntarily report this chemical"
                name="isAlwaysReported"
                tooltip="Always include this chemical on the Tier II report, even when it is below reporting thresholds."
                disabled={isVerified}
                control={form.control}
                checkboxProps={{ sx: { py: 0 } }}
                noErrorDisplay
              />
            )}

            {/* Not gated on isOverThreshold: an over-threshold chemical is exactly
                the case you'd exempt, so this must always be available. */}
            {/*
              Disabled only when checking this box WOULD create a conflict —
              never merely because it is already checked. That keeps a
              pre-existing conflict (both fields set independently before
              this feature existed) resolvable in either direction: a checked
              box always stays clickable-to-uncheck, so nothing is ever left
              in a dead-end state.
            */}
            <CheckboxField
              label="This chemical is exempt from Tier II reporting"
              name="isExempt"
              tooltip={
                overrideBlocksExempt
                  ? "Exclude this chemical from Tier II reporting under a statutory 40 CFR 370.13 exemption. Not available while a reporting-unit override is set."
                  : "Exclude this chemical from Tier II reporting under a statutory 40 CFR 370.13 exemption."
              }
              disabled={isVerified || overrideBlocksExempt}
              control={form.control}
              checkboxProps={{ sx: { py: 0 } }}
              noErrorDisplay
            />

            {isExempt && (
              <DisclosurePanel>
                <FormSelect
                  name="isExemptReason"
                  control={form.control}
                  label="Exemption reason"
                  selectItems={EXEMPT_REASON_ORDER.map((reason) => ({
                    value: reason,
                    display: EXEMPT_REASON_LABELS[reason],
                  }))}
                  rules={{ required: true }}
                />
                <FormTextField
                  name="isExemptNote"
                  control={form.control}
                  label={
                    form.watch("isExemptReason") === ExemptReason.Other
                      ? "Exemption note *"
                      : "Exemption note (optional)"
                  }
                  textFieldProps={{ multiline: true, minRows: 2 }}
                />
              </DisclosurePanel>
            )}

            {/* Staff-only, mirroring StaffReportingUnitOverride's own isStaff
                gating below: a non-staff user must see no trace of this
                checkbox, not merely a disabled one. */}
            {isStaff && offerOverride && (
              <CheckboxField
                label="Override the reporting unit"
                name="reportingUnitOverrideEnabled"
                tooltip={
                  isExempt && !reportingUnitOverrideEnabled
                    ? "Staff only. Not available while this chemical is marked exempt."
                    : "Staff only. Report this chemical in a different unit than aggregation would otherwise use."
                }
                disabled={isVerified || (isExempt && !reportingUnitOverrideEnabled)}
                control={form.control}
                checkboxProps={{ sx: { py: 0 } }}
                // Pumpkin marks this as a staff control, matching the staff
                // nav. Without it this checkbox is indistinguishable from the
                // customer exempt checkbox directly above it.
                tooltipIconColor={theme.palette.nav.staffIcon}
                noErrorDisplay
              />
            )}

            {/* Unticking the checkbox is how an override is removed. That
                collapses this panel and resolves the save as a clear, so the
                removal commits with everything else on this dialog rather
                than landing the moment it is clicked. */}
            {reportingUnitOverrideEnabled && (
              <StaffReportingUnitOverride
                rfc={rfc}
                control={form.control}
                disabled={isVerified ?? false}
              />
            )}
          </Stack>

          {/* After the whole checkbox group rather than under either one: it
              describes the interaction between two of them, so it belongs to
              neither. */}
          {form.watch("isAlwaysReported") && isExempt && (
            <Alert severity="info">
              This chemical is marked exempt but also set to always report — voluntary reporting
              takes precedence, so it will be included on the Tier II report.
            </Alert>
          )}

          {/* The override write is refused server-side; without this the
              dialog would close on a save that never wrote it. */}
          {overrideError && <Alert severity="error">{overrideError}</Alert>}

          {rfc.aggregationErrorMessage && (
            <Alert severity="error">
              {rfc.aggregationErrorMessage}
              {problematicChemicals && (
                <Box sx={{ mt: 1 }}>
                  Fix in catalog:{" "}
                  {problematicChemicals.map((chem, i) => (
                    <Fragment key={chem.id}>
                      {i > 0 && ", "}
                      <Link to={`/o/${tenantId}/chemicals/catalog/chemicals/${chem.id}`}>
                        {chem.name}
                      </Link>
                    </Fragment>
                  ))}
                </Box>
              )}
            </Alert>
          )}
          <SummaryCard rfc={rfc} />

          {rfc.stateFields.length > 0 && (
            <FormProvider {...form}>
              <StateFieldsForm
                namePrefix="stateFields"
                fields={rfc.stateFields}
                context="facilityChemical"
                sx={{ p: theme.spacing(3) }}
              />
            </FormProvider>
          )}

          <MeasurementsTable rfc={rfc} />

          <ProductsTable rfc={rfc} />

          <StorageLocationsTable rfc={rfc} />
          <CatalogLink
            id={rfc.chemical?.id ?? ""}
            name={rfc.chemical?.name ?? ""}
            type="Chemical"
            issues={
              isVerified
                ? []
                : rfc.issues.filter(
                    (issue) =>
                      !anySegment(issue, (s) => s.model === "ReportingFacilityChemicalOrigin")
                  )
            }
          />
          {hasChemicalOrigin && (
            <FacilityChemicalLink
              facilityChemicalId={rfc.origin?.facilityChemicalId ?? ""}
              chemicalId={rfc.chemical?.id ?? ""}
              reportId={reportId}
              facilityId={rfc.facilityId}
              tenantId={tenantId ?? ""}
              name={rfc.chemical?.name ?? ""}
              reportIsVerified={isVerified}
              reportingYear={rfc.reportingYear}
              issues={
                isVerified
                  ? []
                  : rfc.issues.filter((issue) =>
                      anySegment(issue, (s) => s.model === "ReportingFacilityChemicalOrigin")
                    )
              }
            />
          )}
          {uniqueProducts.map(({ catalogProductId, name }) => (
            <CatalogLink
              key={`catalog-${catalogProductId}`}
              id={catalogProductId}
              name={name}
              type="Product"
              issues={
                isVerified
                  ? []
                  : rfc.issues.filter(
                      (issue) =>
                        !anySegment(issue, (s) => s.model === "ReportingFacilityChemicalOrigin")
                    )
              }
            />
          ))}
          {uniqueProducts.map(({ inventoryLinkId, name }) => (
            <ProductInventoryLink
              key={`inventory-${inventoryLinkId}`}
              tenantId={tenantId ?? ""}
              reportId={reportId}
              facilityId={rfc.facilityId}
              productId={inventoryLinkId}
              productName={name}
              reportingYear={rfc.reportingYear}
              issues={
                isVerified
                  ? []
                  : rfc.issues.filter(
                      (issue) =>
                        !anySegment(issue, (s) => s.model === "ReportingFacilityChemicalOrigin")
                    )
              }
            />
          ))}
        </Stack>
      )}
    </Dialog>
  );
};
